import { useEffect, useRef, useState, useCallback } from "react";
import { SESSION_TIMEOUT } from "./constants";
import { readIdleClock, touchIdleClock, IDLE_STORAGE_KEY } from "./idleClock";

// How often we re-evaluate idle state while the tab is awake. Drives the
// warning countdown; in a backgrounded tab the browser throttles this, which is
// fine — visibilitychange/focus re-check the moment the tab wakes.
const TICK_MS = 1000;

// Cap how often real activity is written to localStorage, so a stream of
// mousemove events doesn't spam writes / cross-tab storage events. Leading-edge:
// the first activity after a lull writes immediately; subsequent moves within
// the window are ignored.
const ACTIVITY_WRITE_THROTTLE_MS = 5000;

export function useIdleTimeout(onTimeout: () => void, enabled: boolean) {
  const [showWarning, setShowWarning] = useState(false);
  const showWarningRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  const firedRef = useRef(false);
  const lastWriteRef = useRef(0);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  });

  // Keep ref and state in sync so the activity handler can read warning state
  // synchronously without re-subscribing.
  const applyWarning = useCallback((val: boolean) => {
    showWarningRef.current = val;
    setShowWarning(val);
  }, []);

  // Record activity into the shared clock (throttled unless forced) and clear
  // any visible warning. While the warning is up, passive activity is IGNORED —
  // the user must explicitly click "Stay signed in" (force) to stay, so the
  // modal doesn't vanish on an incidental mouse move. Genuine activity in
  // another tab still dismisses it, via evaluate() reading the shared clock.
  const markActivity = useCallback(
    (force: boolean) => {
      if (!force && showWarningRef.current) return;
      const now = Date.now();
      if (!force && now - lastWriteRef.current < ACTIVITY_WRITE_THROTTLE_MS)
        return;
      lastWriteRef.current = now;
      touchIdleClock();
      applyWarning(false);
    },
    [applyWarning],
  );

  const resetTimer = useCallback(() => markActivity(true), [markActivity]);

  useEffect(() => {
    if (!enabled) {
      applyWarning(false);
      firedRef.current = false;
      return;
    }

    firedRef.current = false;
    // Fresh session with no stored activity yet — seed it. A reload of an
    // existing session finds a recent timestamp here and keeps counting from it.
    if (readIdleClock() === 0) touchIdleClock();

    const evaluate = () => {
      if (firedRef.current) return;
      const elapsed = Date.now() - readIdleClock();
      const { IDLE_MS, WARN_BEFORE_MS } = SESSION_TIMEOUT;
      if (elapsed >= IDLE_MS) {
        firedRef.current = true;
        applyWarning(false);
        onTimeoutRef.current();
        return;
      }
      applyWarning(elapsed >= IDLE_MS - WARN_BEFORE_MS);
    };

    const EVENTS = [
      "mousemove",
      "keydown",
      "mousedown",
      "touchstart",
      "scroll",
    ] as const;
    const onActivity = () => markActivity(false);
    EVENTS.forEach((e) =>
      window.addEventListener(e, onActivity, { passive: true }),
    );

    // Another tab recorded activity (or signed out, clearing the key) — re-check.
    const onStorage = (e: StorageEvent) => {
      if (e.key === IDLE_STORAGE_KEY) evaluate();
    };
    window.addEventListener("storage", onStorage);

    // Coming back to a tab that was backgrounded/suspended: re-check now instead
    // of waiting for the throttled interval to catch up.
    const onWake = () => {
      if (document.visibilityState === "visible") evaluate();
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);

    evaluate();
    const tick = setInterval(evaluate, TICK_MS);

    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      clearInterval(tick);
    };
  }, [enabled, markActivity, applyWarning]);

  return { showWarning, resetTimer };
}
